/**
 * React Hooks for Feature Flags
 *
 * Provides React hooks for accessing feature flags in components.
 * Uses TanStack Query for caching, refetching, and state management.
 *
 * Hooks available:
 * - useFeatureFlag(name): Check a single feature flag
 * - useFeatureFlags(): Get all feature flags
 * - useUserFlagOverride(name): Get user-specific override for a flag
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type {
  FeatureFlag,
  FeatureFlagResult,
  UserFeatureFlag,
} from '@/lib/featureFlags';
import {
  getFeatureFlag,
  getAllFeatureFlags as fetchAllFlags,
  getUserFlags,
  getFeatureFlagForUser,
  clearFlagCache,
} from '@/lib/featureFlags';

// ============================================================================
// Types
// ============================================================================

/**
 * Result of useFeatureFlag hook
 */
export interface UseFeatureFlagResult {
  enabled: boolean;
  variant?: string;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Result of useFeatureFlags hook
 */
export interface UseFeatureFlagsResult {
  flags: Record<string, boolean>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Result of useUserFlagOverride hook
 */
export interface UseUserFlagOverrideResult {
  override: UserFeatureFlag | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  setOverride: (isEnabled: boolean | null, variant?: string) => Promise<boolean>;
  removeOverride: () => Promise<boolean>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the current user ID from Supabase auth
 * @returns User ID or null if not authenticated
 */
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error || !user) {
      return null;
    }

    return user.id;
  } catch {
    return null;
  }
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React hook to check if a feature flag is enabled for the current user
 *
 * @param name - Name of the feature flag to check
 * @param options - Optional configuration
 * @returns Feature flag state with enabled status, loading state, and error
 *
 * @example
 * ```tsx
 * const { enabled, isLoading } = useFeatureFlag('color_refined');
 *
 * if (isLoading) return <Spinner />;
 * return enabled ? <RefinedTheme /> : <LegacyTheme />;
 * ```
 */
export function useFeatureFlag(
  name: string,
  options?: {
    /**
     * Whether to refetch the flag on window focus
     * @default true
     */
    refetchOnWindowFocus?: boolean;

    /**
     * Stale time in milliseconds (how long to consider data fresh)
     * @default 300000 (5 minutes)
     */
    staleTime?: number;

    /**
     * Whether to enable the query (set to false to skip fetching)
     * @default true
     */
    enabled?: boolean;
  }
): UseFeatureFlagResult {
  const [userId, setUserId] = useState<string | null>(null);
  const [userIdLoading, setUserIdLoading] = useState(true);

  // Fetch user ID on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchUserId() {
      setUserIdLoading(true);
      const id = await getCurrentUserId();
      if (!cancelled) {
        setUserId(id);
        setUserIdLoading(false);
      }
    }

    fetchUserId();

    return () => {
      cancelled = true;
    };
  }, []);

  // Query function that checks the flag for the current user
  const queryFn = async (): Promise<FeatureFlagResult> => {
    if (userId) {
      return await getFeatureFlagForUser(userId, name);
    }

    // No user - check the global flag only
    const flag = await getFeatureFlag(name);

    if (!flag) {
      return {
        enabled: false,
        source: 'default',
      };
    }

    return {
      enabled: flag.is_enabled,
      variant: (flag.config.variant as string) || undefined,
      source: flag.is_enabled ? 'global' : 'default',
    };
  };

  const {
    data: result,
    isLoading: queryLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['featureFlag', name, userId],
    queryFn,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    enabled: (options?.enabled ?? true) && !userIdLoading,
    retry: 1,
  });

  return {
    enabled: result?.enabled ?? false,
    variant: result?.variant,
    isLoading: userIdLoading || queryLoading,
    error,
    refetch: () => refetch(),
  };
}

/**
 * React hook to get all feature flags at once
 *
 * @param options - Optional configuration
 * @returns All feature flags as a record object
 *
 * @example
 * ```tsx
 * const { flags, isLoading } = useFeatureFlags();
 * console.log(flags.color_refined, flags.new_dashboard);
 * ```
 */
export function useFeatureFlags(options?: {
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
}): UseFeatureFlagsResult {
  const queryFn = async (): Promise<Record<string, boolean>> => {
    const flags = await fetchAllFlags();

    // Convert array to record object
    const record: Record<string, boolean> = {};
    flags.forEach(flag => {
      record[flag.name] = flag.is_enabled;
    });

    return record;
  };

  const {
    data: flags = {},
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['featureFlags'],
    queryFn,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    enabled: options?.enabled ?? true,
    retry: 1,
  });

  return {
    flags,
    isLoading,
    error,
    refetch: () => refetch(),
  };
}

/**
 * React hook to get user-specific overrides for feature flags
 *
 * @returns Array of user's feature flag overrides
 *
 * @example
 * ```tsx
 * const { overrides, isLoading } = useUserFlagOverrides();
 * overrides.forEach(o => console.log(o.flag_name, o.is_enabled));
 * ```
 */
export function useUserFlagOverrides(options?: {
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}): {
  overrides: UserFeatureFlag[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const [userId, setUserId] = useState<string | null>(null);

  // Fetch user ID on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchUserId() {
      const id = await getCurrentUserId();
      if (!cancelled) {
        setUserId(id);
      }
    }

    fetchUserId();

    return () => {
      cancelled = true;
    };
  }, []);

  const queryFn = async (): Promise<UserFeatureFlag[]> => {
    if (!userId) {
      return [];
    }
    return await getUserFlags(userId);
  };

  const {
    data: overrides = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userFlagOverrides', userId],
    queryFn,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? true,
    enabled: !!userId,
    retry: 1,
  });

  return {
    overrides,
    isLoading,
    error,
    refetch: () => refetch(),
  };
}

/**
 * React hook to get and manage a user-specific override for a flag
 *
 * @param flagName - Name of the feature flag
 * @returns User override with functions to set/remove it
 *
 * @example
 * ```tsx
 * const { override, setOverride, removeOverride } = useUserFlagOverride('color_refined');
 *
 * // Enable for this user
 * await setOverride(true, 'refined');
 *
 * // Remove override
 * await removeOverride();
 * ```
 */
export function useUserFlagOverride(flagName: string): UseUserFlagOverrideResult {
  const [userId, setUserId] = useState<string | null>(null);

  // Fetch user ID on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchUserId() {
      const id = await getCurrentUserId();
      if (!cancelled) {
        setUserId(id);
      }
    }

    fetchUserId();

    return () => {
      cancelled = true;
    };
  }, []);

  const queryFn = async (): Promise<UserFeatureFlag | null> => {
    if (!userId) {
      return null;
    }

    const overrides = await getUserFlags(userId);
    return overrides.find(o => o.flag_name === flagName) || null;
  };

  const {
    data: override,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userFlagOverride', userId, flagName],
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    enabled: !!userId,
    retry: 1,
  });

  const setOverride = async (isEnabled: boolean | null, variant?: string): Promise<boolean> => {
    if (!userId) {
      return false;
    }

    // Import dynamically to avoid circular dependency
    const { setUserFlagOverride } = await import('@/lib/featureFlags');

    const success = await setUserFlagOverride(userId, flagName, isEnabled, variant);

    if (success) {
      refetch();
    }

    return success;
  };

  const removeOverride = async (): Promise<boolean> => {
    if (!userId) {
      return false;
    }

    // Import dynamically to avoid circular dependency
    const { removeUserFlagOverride } = await import('@/lib/featureFlags');

    const success = await removeUserFlagOverride(userId, flagName);

    if (success) {
      refetch();
    }

    return success;
  };

  return {
    override,
    isLoading,
    error,
    refetch: () => refetch(),
    setOverride,
    removeOverride,
  };
}

/**
 * React hook to invalidate the feature flag cache
 * Useful for forcing a refresh of flag data
 *
 * @example
 * ```tsx
 * const { invalidateFlags } = useFlagInvalidator();
 *
 * // After changing a flag in admin panel
 * await updateFeatureFlag('color_refined', { is_enabled: true });
 * invalidateFlags();
 * ```
 */
export function useFlagInvalidator(): {
  invalidateFlags: () => void;
  invalidateFlag: (name: string) => void;
} {
  const invalidateFlags = () => {
    clearFlagCache();
  };

  const invalidateFlag = (name: string) => {
    clearFlagCache();
  };

  return {
    invalidateFlags,
    invalidateFlag,
  };
}

/**
 * React hook that provides feature flag utilities
 * Combines common feature flag operations into a single hook
 *
 * @example
 * ```tsx
 * const { isEnabled, isLoading, invalidate } = useFeatureFlags();
 *
 * if (isEnabled('new_feature')) {
 *   return <NewFeature />;
 * }
 * ```
 */
export function useFeatureFlagUtils(): {
  isEnabled: (flagName: string) => boolean;
  isLoading: boolean;
  flags: Record<string, boolean>;
  invalidate: () => void;
} {
  const { flags, isLoading } = useFeatureFlags();
  const { invalidateFlags } = useFlagInvalidator();

  return {
    isEnabled: (flagName: string) => flags[flagName] ?? false,
    isLoading,
    flags,
    invalidate: invalidateFlags,
  };
}
